import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInbox } from '../../hooks/use-inbox.js';
import { formatUtcInTimezone } from '../../lib/time.js';

const KIND_META = {
  announcement: { icon: 'fa-bullhorn',           tone: 'text-accent' },
  alert:        { icon: 'fa-triangle-exclamation', tone: 'text-warning' },
  order:        { icon: 'fa-clipboard-list',     tone: 'text-rose-700' },
  system:       { icon: 'fa-gear',               tone: 'text-mist' },
};

const PRIORITY_TONE = {
  routine:   'border-line     bg-canvas      text-mist',
  priority:  'border-accent/30 bg-accent-soft text-accent',
  immediate: 'border-warning/30 bg-warning-soft text-warning',
  flash:     'border-rose-200  bg-rose-soft   text-rose-700',
};

/**
 * Topbar-less bell. Drops into the sidebar's user pill area. Click toggles
 * a dropdown panel listing the most recent unread + read items.
 */
export function BellWidget({ viewerTimezone }) {
  const { items, unreadCount, connected, markRead, dismiss, markAllRead } = useInbox();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (!ref.current?.contains(e.target)) setOpen(false);
    }
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const visible = items.slice(0, 12);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={
          'relative inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors ' +
          (unreadCount > 0
            ? 'bg-accent-soft text-accent hover:bg-accent-soft'
            : 'text-mist hover:bg-canvas hover:text-ink')
        }
        title={`${unreadCount} unread`}
        aria-label="Notifications"
      >
        <i className={`fa-${unreadCount > 0 ? 'solid' : 'regular'} fa-bell text-[14px]`} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
        {!connected && (
          <span
            className="absolute -right-1 -bottom-1 h-2 w-2 rounded-full border border-white bg-warning"
            title="Reconnecting…"
          />
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-30 mt-2 w-[340px] rounded-2xl border border-line bg-white shadow-lg">
          <header className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <div className="flex items-center gap-2 text-sm font-semibold text-ink">
              <i className="fa-regular fa-bell text-mist" aria-hidden="true" />
              Notifications
              {unreadCount > 0 && (
                <span className="rounded-full bg-rose-soft px-2 py-0.5 text-[10px] font-semibold text-rose-700">
                  {unreadCount} unread
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={markAllRead}
                  className="text-[11px] text-accent hover:underline"
                >
                  Mark all read
                </button>
              )}
              <Link
                to="/notifications"
                onClick={() => setOpen(false)}
                className="text-[11px] text-mist hover:text-ink"
                title="Open notifications page"
              >
                <i className="fa-solid fa-arrow-up-right-from-square text-[10px]" aria-hidden="true" />
              </Link>
            </div>
          </header>

          {visible.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-mist">
              <i className="fa-regular fa-envelope-open mb-2 block text-2xl" aria-hidden="true" />
              No notifications.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-line scrollbar-thin">
              {visible.map((n) => {
                const km = KIND_META[n.kind] || KIND_META.announcement;
                const isUnread = !n.delivery?.status || (n.delivery.status !== 'read' && n.delivery.status !== 'dismissed');
                return (
                  <li
                    key={n.id}
                    className={
                      'relative px-4 py-3 hover:bg-canvas/40 ' +
                      (isUnread ? 'bg-accent-soft/30' : '')
                    }
                  >
                    <div className="flex items-start gap-2">
                      <div className={'mt-0.5 ' + km.tone}>
                        <i className={`fa-solid ${km.icon}`} aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className={'truncate text-[13px] ' + (isUnread ? 'font-semibold text-ink' : 'text-mist')}>
                            {n.title}
                          </span>
                          {n.priority !== 'routine' && (
                            <span className={`rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${PRIORITY_TONE[n.priority] || PRIORITY_TONE.routine}`}>
                              {n.priority}
                            </span>
                          )}
                        </div>
                        {n.body && (
                          <p className={'mt-0.5 text-[12px] line-clamp-2 ' + (isUnread ? 'text-ink/80' : 'text-mist')}>
                            {n.body}
                          </p>
                        )}
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-mist">
                          <span>{formatUtcInTimezone(n.createdAt, viewerTimezone)}</span>
                          {n.sentByLogin && <span>· by {n.sentByLogin}</span>}
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col items-center gap-1">
                        {isUnread && (
                          <button
                            type="button"
                            onClick={() => markRead(n.id)}
                            title="Mark as read"
                            className="text-mist transition-colors hover:text-accent"
                          >
                            <i className="fa-solid fa-check text-[11px]" aria-hidden="true" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => dismiss(n.id)}
                          title="Dismiss"
                          className="text-mist transition-colors hover:text-rose-600"
                        >
                          <i className="fa-solid fa-xmark text-[11px]" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
